import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import {
  IcoCheck,
  IcoChevronDown,
  IcoChevronLeft,
  IcoChevronRight,
  IcoMail,
  IcoUsers,
} from "../../components/icons";
import { BrandedLayout } from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import type { HierarchySegment, TeamMember } from "../../server/rpc/team";
import type { MimicState } from "../../stores/mimic";
import type { OrganizationListItem, User } from "../../types";
import { isAdmin } from "../../types";
import { formatDateShort, formatPeriodRangeShort } from "../../utils/format";
import {
  formatWeekLabel,
  getAdjacentWeek,
  getMondayFromWeekKey,
  getWeekKeyFromDate,
} from "../../utils/period-grouping";

// Filter options for review status
type ReviewFilter = "all" | "needs-review" | "reviewed";

type MoodTrendPeriod = {
  periodStart: string;
  avgRating: number;
  responseCount: number;
  totalMembers: number;
};

type TeamPageState = {
  currentUser: User;
  mimic: MimicState;
  org: OrganizationListItem;
  isOrgAdmin: boolean;
  /** True when admin is viewing the root team page (all org members, grouped) */
  isAdminRootView: boolean;
  teamMembers: TeamMember[];
  hasReports: boolean;
  currentMemberId: string;
  currentMemberName: string;
  // Period navigation
  weekKey: string;
  weekLabel: string;
  periodLabel: string | null;
  periodContainsToday: boolean;
  prevWeekKey: string;
  nextWeekKey: string;
  isCurrentWeek: boolean;
  currentPeriodWeekKey: string;
  // Sub-team drill-down
  viewingManagerId: string | null;
  viewingManagerName: string | null;
  breadcrumbs: HierarchySegment[];
  // Review filter
  filter: ReviewFilter;
  // Aggregate mood trend
  moodTrend: MoodTrendPeriod[];
};

export async function load(args: LoaderArgs): Promise<TeamPageState> {
  const orgId = args.params.orgId ?? "";

  const requestedWeek = args.url.searchParams.get("week");

  // Get manager ID for sub-team drill-down (null = show current user's team)
  const viewingManagerId = args.url.searchParams.get("manager");

  // Get filter from URL params, default to "all"
  const filterParam = args.url.searchParams.get("filter");
  const filter: ReviewFilter =
    filterParam === "needs-review" || filterParam === "reviewed"
      ? filterParam
      : "all";

  // Stage 1: Auth check must complete first to ensure clean /login redirect
  const [authResult, orgsResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const org = orgsResult.organizations.find((o) => o.id === orgId);
  if (!org) {
    throw new RedirectError("/");
  }

  const isOrgAdmin = isAdmin(org.role);

  // Determine the week to show. If no explicit ?week= param, default to the
  // server-recommended period (holds on the just-ended period until ~65%
  // into the new period — see getReviewPeriod). The "Today" button still
  // jumps to the actual current period for explicit forward navigation.
  const currentCalendarWeekKey = getWeekKeyFromDate(new Date());
  const reviewPeriod = await rpc.team.getReviewPeriod({
    workspaceId: orgId,
  });
  const currentPeriodWeekKey = reviewPeriod.currentPeriodStart
    ? getWeekKeyFromDate(new Date(reviewPeriod.currentPeriodStart))
    : currentCalendarWeekKey;
  const defaultWeekKey = reviewPeriod.defaultPeriodStart
    ? getWeekKeyFromDate(new Date(reviewPeriod.defaultPeriodStart))
    : currentPeriodWeekKey;
  let weekKey: string;
  if (requestedWeek) {
    weekKey = requestedWeek;
  } else {
    weekKey = defaultWeekKey;
  }
  const periodStart = getMondayFromWeekKey(weekKey);

  // Stage 2: Remaining data calls in parallel (auth is validated)
  const [teamResult, hierarchyResult, moodTrendResult] = await Promise.all([
    rpc.team.getMyTeam({
      workspaceId: orgId,
      periodStart: periodStart.toISOString(),
      managerId: viewingManagerId || undefined,
      // Admin root view: fetch all org members instead of just direct reports
      allMembers: isOrgAdmin && !viewingManagerId ? true : undefined,
    }),
    viewingManagerId
      ? rpc.team.getTeamHierarchy({
          workspaceId: orgId,
          targetMemberId: viewingManagerId,
        })
      : Promise.resolve(null),
    rpc.team
      .getTeamMoodTrend({
        workspaceId: orgId,
        periodCount: 12,
        managerId: viewingManagerId || undefined,
      })
      .catch(() => ({ periods: [] as MoodTrendPeriod[] })),
  ]);

  // If user has no reports and not viewing sub-team, redirect to home
  if (!teamResult.hasReports && !viewingManagerId) {
    throw new RedirectError(`/orgs/${orgId}`);
  }

  const breadcrumbs: HierarchySegment[] = hierarchyResult?.segments ?? [];

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    org,
    isOrgAdmin,
    isAdminRootView: isOrgAdmin && !viewingManagerId,
    teamMembers: teamResult.team,
    hasReports: teamResult.hasReports,
    currentMemberId: teamResult.currentMember.id,
    currentMemberName: teamResult.currentMember.name,
    // Period navigation
    weekKey,
    weekLabel: formatWeekLabel(weekKey),
    periodLabel: teamResult.formPeriod
      ? formatPeriodRangeShort(
          new Date(teamResult.formPeriod.start),
          new Date(teamResult.formPeriod.end),
        )
      : null,
    periodContainsToday: teamResult.formPeriod
      ? (() => {
          const now = new Date();
          const start = new Date(teamResult.formPeriod.start);
          const end = new Date(teamResult.formPeriod.end);
          return now >= start && now <= end;
        })()
      : false,
    prevWeekKey: getAdjacentWeek(weekKey, "prev"),
    nextWeekKey: getAdjacentWeek(weekKey, "next"),
    isCurrentWeek: weekKey === currentPeriodWeekKey,
    currentPeriodWeekKey,
    // Sub-team drill-down
    viewingManagerId,
    viewingManagerName: teamResult.manager?.name ?? null,
    breadcrumbs,
    // Review filter
    filter,
    // Aggregate mood trend
    moodTrend: moodTrendResult.periods,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Team");
  const {
    currentUser,
    mimic,
    org,
    isOrgAdmin,
    isAdminRootView,
    teamMembers,
    hasReports,
    weekKey,
    weekLabel,
    prevWeekKey,
    nextWeekKey,
    isCurrentWeek,
    currentPeriodWeekKey,
    viewingManagerId,
    viewingManagerName,
    breadcrumbs,
    filter,
    moodTrend,
  } = state.value;

  // Build URL with optional manager and filter params
  function buildUrl(params: {
    week?: string;
    manager?: string | null;
    filter?: ReviewFilter;
  }) {
    const url = new URL(`/orgs/${org.id}/team`, window.location.origin);
    const week = "week" in params ? params.week : weekKey;
    if (week) url.searchParams.set("week", week);
    const manager =
      params.manager !== undefined ? params.manager : viewingManagerId;
    if (manager) url.searchParams.set("manager", manager);
    const filterValue = params.filter ?? filter;
    if (filterValue && filterValue !== "all") {
      url.searchParams.set("filter", filterValue);
    }
    return url.pathname + url.search;
  }

  // Separate members by submission status
  const allSubmitted = teamMembers.filter((m) => m.latestSubmission !== null);
  const allWaiting = teamMembers.filter((m) => m.latestSubmission === null);

  // Split waiting into truly waiting vs on vacation
  const onVacation = teamMembers.filter((m) => m.isOnVacation);
  const waiting = allWaiting.filter((m) => !m.isOnVacation);

  // Further separate submitted by review status
  const needsReview = allSubmitted.filter(
    (m) => m.latestSubmission?.reviewedAt === null,
  );
  const reviewed = allSubmitted.filter(
    (m) => m.latestSubmission?.reviewedAt !== null,
  );

  // Apply filter to submitted list
  const submitted =
    filter === "needs-review"
      ? needsReview
      : filter === "reviewed"
        ? reviewed
        : allSubmitted;

  // Compute counts for tabs
  const filterCounts = {
    all: allSubmitted.length,
    "needs-review": needsReview.length,
    reviewed: reviewed.length,
  };

  // Vacation-aware counts: exclude vacationing members from denominator
  const totalCount = teamMembers.length;
  const activeCount =
    totalCount - onVacation.filter((m) => m.latestSubmission === null).length;
  const submittedCount = allSubmitted.length;
  const completionPercent =
    activeCount > 0 ? Math.round((submittedCount / activeCount) * 100) : 0;
  const awayCount = onVacation.length;

  // Hide waiting section when filter is active (filter only applies to submitted)
  const showWaiting = filter === "all" && waiting.length > 0;
  const showAway = filter === "all" && onVacation.length > 0;

  // Expandable completion breakdown
  const showCompletionDetail = useSignal(false);

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={org.id}
      orgName={org.name}
      brandColor={org.brandColor}
      activeTab="team"
      isAdmin={isOrgAdmin}
      formCount={org.formCount}
      hasReports={hasReports}
    >
      {/* Breadcrumb Navigation (when viewing sub-team) */}
      {breadcrumbs.length > 0 && (
        <nav class="flex items-center gap-1 text-sm mb-4">
          <a
            href={buildUrl({ manager: null })}
            class="text-text-muted hover:text-[var(--brand-600)] transition-colors"
          >
            {isOrgAdmin ? "All Members" : "My Team"}
          </a>
          {breadcrumbs.map((segment, idx) => (
            <span key={segment.memberId} class="flex items-center gap-1">
              <IcoChevronRight class="size-4 text-text-subtle" />
              {idx === breadcrumbs.length - 1 ? (
                <span class="text-text font-medium">
                  {segment.memberName || "Unknown"}'s Team
                </span>
              ) : (
                <a
                  href={buildUrl({ manager: segment.memberId })}
                  class="text-text-muted hover:text-[var(--brand-600)] transition-colors"
                >
                  {segment.memberName || "Unknown"}
                </a>
              )}
            </span>
          ))}
        </nav>
      )}

      {/* Page Header with Period Navigation */}
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-text">
            {viewingManagerName
              ? `${viewingManagerName}'s Team`
              : isAdminRootView
                ? "All Members"
                : "Team Check-ins"}
          </h1>
          <p class="text-text-muted mt-1">
            {state.value.periodLabel || weekLabel}
            {state.value.periodContainsToday && (
              <span class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-[var(--brand-100)] text-[var(--brand-700)]">
                today
              </span>
            )}
          </p>
        </div>
        <div class="flex items-center gap-1 bg-surface-muted rounded-lg p-1">
          <a
            href={buildUrl({ week: prevWeekKey })}
            class="p-2 rounded-md hover:bg-surface transition-colors"
            title={`Previous period: ${formatWeekLabel(prevWeekKey)}`}
          >
            <IcoChevronLeft class="size-5 text-text-muted" />
          </a>
          {!isCurrentWeek && (
            <a
              href={buildUrl({
                week: currentPeriodWeekKey,
                manager: viewingManagerId,
              })}
              class="px-3 py-1.5 text-sm font-medium text-[var(--brand-600)] hover:bg-[var(--brand-50)] rounded-md transition-colors"
              title="Jump to current period"
            >
              Today
            </a>
          )}
          <a
            href={buildUrl({ week: nextWeekKey })}
            class={`p-2 rounded-md transition-colors ${
              isCurrentWeek
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-surface"
            }`}
            title={
              isCurrentWeek
                ? "Already on current period"
                : `Next period: ${formatWeekLabel(nextWeekKey)}`
            }
            onClick={isCurrentWeek ? (e) => e.preventDefault() : undefined}
          >
            <IcoChevronRight class="size-5 text-text-muted" />
          </a>
        </div>
      </div>

      {/* Aggregate Mood Trend Chart */}
      {moodTrend.length >= 2 && <TeamMoodChart periods={moodTrend} />}

      {/* Completion Summary (clickable to expand name breakdown) */}
      <div class="rounded-xl border border-border bg-surface p-5 mb-6">
        <button
          type="button"
          onClick={() => {
            showCompletionDetail.value = !showCompletionDetail.value;
          }}
          class="w-full text-left"
        >
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-3">
              <div class="size-10 rounded-full bg-[var(--brand-100)] flex items-center justify-center">
                <IcoUsers class="size-5 text-[var(--brand-600)]" />
              </div>
              <div>
                <span class="text-lg font-semibold text-text">
                  {submittedCount} of {activeCount}
                </span>
                <span class="text-text-muted ml-1">submitted</span>
                {awayCount > 0 && (
                  <span class="text-sm text-sky-600 dark:text-sky-400 ml-2">
                    ({awayCount} away)
                  </span>
                )}
              </div>
            </div>
            <div class="flex items-center gap-2">
              <span
                class={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                  completionPercent === 100
                    ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                    : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                }`}
              >
                {completionPercent === 100 && <IcoCheck class="size-4" />}
                {completionPercent}%
              </span>
              <IcoChevronDown
                class={`size-5 text-text-muted transition-transform ${showCompletionDetail.value ? "" : "-rotate-90"}`}
              />
            </div>
          </div>
          <div class="h-2 bg-surface-muted rounded-full overflow-hidden">
            <div
              class={`h-full rounded-full transition-all ${
                completionPercent === 100
                  ? "bg-green-500"
                  : "bg-[var(--brand-500)]"
              }`}
              style={{ width: `${completionPercent}%` }}
            />
          </div>
        </button>

        {/* Expanded name breakdown */}
        {showCompletionDetail.value && (
          <div
            class={`grid grid-cols-1 ${awayCount > 0 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-4 mt-4 pt-4 border-t border-border`}
          >
            {/* Waiting column */}
            <div>
              <h3 class="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">
                Waiting ({waiting.length})
              </h3>
              {waiting.length === 0 ? (
                <p class="text-sm text-green-600 dark:text-green-400 flex items-center gap-1.5">
                  <IcoCheck class="size-4" />
                  {awayCount > 0
                    ? "All available members submitted"
                    : "Everyone submitted"}
                </p>
              ) : (
                <div class="space-y-0.5">
                  {waiting.map((m) => (
                    <CompletionNameRow
                      key={m.id}
                      member={m}
                      orgId={org.id}
                      variant="waiting"
                    />
                  ))}
                </div>
              )}
            </div>
            {/* Away column */}
            {awayCount > 0 && (
              <div>
                <h3 class="text-xs font-semibold text-sky-600 dark:text-sky-400 uppercase tracking-wide mb-2">
                  Away ({awayCount})
                </h3>
                <div class="space-y-0.5">
                  {onVacation.map((m) => (
                    <CompletionNameRow
                      key={m.id}
                      member={m}
                      orgId={org.id}
                      variant="away"
                    />
                  ))}
                </div>
              </div>
            )}
            {/* Submitted column */}
            <div>
              <h3 class="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">
                Submitted ({allSubmitted.length})
              </h3>
              {allSubmitted.length === 0 ? (
                <p class="text-sm text-text-muted">No submissions yet</p>
              ) : (
                <div class="space-y-0.5">
                  {allSubmitted.map((m) => (
                    <CompletionNameRow
                      key={m.id}
                      member={m}
                      orgId={org.id}
                      variant="submitted"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Filter Tabs + Review Button */}
      {allSubmitted.length > 0 && (
        <div class="flex items-center gap-3 mb-6">
          <div class="flex gap-1 p-1 bg-surface-muted rounded-lg w-fit">
            <FilterTab
              label="All"
              count={filterCounts.all}
              isActive={filter === "all"}
              href={buildUrl({ filter: "all" })}
            />
            <FilterTab
              label="Needs Review"
              count={filterCounts["needs-review"]}
              isActive={filter === "needs-review"}
              href={buildUrl({ filter: "needs-review" })}
              showDot={filterCounts["needs-review"] > 0}
            />
            <FilterTab
              label="Reviewed"
              count={filterCounts.reviewed}
              isActive={filter === "reviewed"}
              href={buildUrl({ filter: "reviewed" })}
            />
          </div>
          {needsReview.length > 0 && (
            <a
              href={`/orgs/${org.id}/feed?week=${weekKey}${isAdminRootView ? "&scope=all" : isOrgAdmin && viewingManagerId ? "&scope=team" : ""}`}
              class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--brand-600)] text-white hover:bg-[var(--brand-700)] transition-colors shadow-sm"
            >
              Start Reviewing ({needsReview.length})
            </a>
          )}
        </div>
      )}

      {/* Admin grouped view: group by manager */}
      {isAdminRootView ? (
        <AdminGroupedView
          teamMembers={teamMembers}
          filter={filter}
          orgId={org.id}
          weekKey={weekKey}
          currentMemberId={state.value.currentMemberId}
          currentMemberName={state.value.currentMemberName}
        />
      ) : (
        <FlatTeamView
          submitted={submitted}
          waiting={waiting}
          onVacation={onVacation}
          showWaiting={showWaiting}
          showAway={showAway}
          filter={filter}
          orgId={org.id}
          weekKey={weekKey}
          allSubmitted={allSubmitted}
          needsReview={needsReview}
        />
      )}
    </BrandedLayout>
  );
}

// ============================================================================
// Flat Team View (non-admin, or admin drill-down)
// ============================================================================

function FlatTeamView({
  submitted,
  waiting,
  onVacation,
  showWaiting,
  showAway,
  filter,
  orgId,
  weekKey,
  allSubmitted,
  needsReview,
}: {
  submitted: TeamMember[];
  waiting: TeamMember[];
  onVacation: TeamMember[];
  showWaiting: boolean;
  showAway: boolean;
  filter: ReviewFilter;
  orgId: string;
  weekKey: string;
  allSubmitted: TeamMember[];
  needsReview: TeamMember[];
}) {
  return (
    <>
      {/* Needs Attention Section (waiting for submission, excluding vacation) */}
      {showWaiting && (
        <section class="mb-6">
          <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-3">
            Needs Attention ({waiting.length})
          </h2>
          <div class="rounded-xl border border-border bg-surface overflow-hidden divide-y divide-border">
            {waiting.map((member) => (
              <TeamMemberRow
                key={member.id}
                member={member}
                orgId={orgId}
                weekKey={weekKey}
                status="waiting"
              />
            ))}
          </div>
        </section>
      )}

      {/* Away Section (on vacation) */}
      {showAway && (
        <section class="mb-6">
          <h2 class="text-sm font-semibold text-sky-600 dark:text-sky-400 uppercase tracking-wide mb-3">
            Away ({onVacation.length})
          </h2>
          <div class="rounded-xl border border-sky-200 dark:border-sky-800/50 bg-sky-50/50 dark:bg-sky-950/20 overflow-hidden divide-y divide-sky-200 dark:divide-sky-800/50">
            {onVacation.map((member) => (
              <TeamMemberRow
                key={member.id}
                member={member}
                orgId={orgId}
                weekKey={weekKey}
                status="away"
              />
            ))}
          </div>
        </section>
      )}

      {/* Submitted Section */}
      {submitted.length > 0 && (
        <section class="mb-6">
          <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-3">
            {filter === "needs-review"
              ? `Needs Review (${submitted.length})`
              : filter === "reviewed"
                ? `Reviewed (${submitted.length})`
                : `Submitted (${submitted.length})`}
          </h2>
          <div class="rounded-xl border border-border bg-surface overflow-hidden divide-y divide-border">
            {submitted.map((member) => (
              <TeamMemberRow
                key={member.id}
                member={member}
                orgId={orgId}
                weekKey={weekKey}
                status={
                  member.latestSubmission?.reviewedAt ? "reviewed" : "submitted"
                }
              />
            ))}
          </div>
        </section>
      )}

      {/* Empty Filter State */}
      {submitted.length === 0 && filter !== "all" && (
        <div class="flex flex-col items-center justify-center gap-3 p-8 rounded-xl border border-border bg-surface text-center">
          <div class="size-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <IcoCheck class="size-6 text-green-600 dark:text-green-400" />
          </div>
          {filter === "needs-review" ? (
            <>
              <h3 class="font-medium text-text">All caught up!</h3>
              <p class="text-text-muted text-sm max-w-sm">
                You've reviewed all submitted check-ins for this week.
              </p>
            </>
          ) : (
            <>
              <h3 class="font-medium text-text">No reviewed check-ins yet</h3>
              <p class="text-text-muted text-sm max-w-sm">
                Check-ins you've reviewed will appear here.
              </p>
            </>
          )}
        </div>
      )}

      {/* All Complete State */}
      {filter === "all" &&
        waiting.length === 0 &&
        allSubmitted.length > 0 &&
        needsReview.length === 0 && (
          <div class="flex items-center justify-center gap-3 p-6 rounded-xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30">
            <div class="size-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
              <IcoCheck class="size-5 text-green-600 dark:text-green-400" />
            </div>
            <span class="text-lg font-medium text-green-700 dark:text-green-400">
              All check-ins submitted and reviewed!
            </span>
          </div>
        )}
    </>
  );
}

// ============================================================================
// Admin Grouped View (all org members grouped by manager)
// ============================================================================

type ManagerGroup = {
  managerId: string | null;
  managerName: string;
  members: TeamMember[];
};

function AdminGroupedView({
  teamMembers,
  filter,
  orgId,
  weekKey,
  currentMemberId,
  currentMemberName,
}: {
  teamMembers: TeamMember[];
  filter: ReviewFilter;
  orgId: string;
  weekKey: string;
  currentMemberId: string;
  currentMemberName: string;
}) {
  // Build manager name lookup — seed with the current admin (excluded from teamMembers)
  const managerNameMap = new Map<string, string>();
  managerNameMap.set(currentMemberId, currentMemberName);
  for (const m of teamMembers) {
    managerNameMap.set(m.id, m.name || m.email);
  }

  // Compute submission stats from full (unfiltered) teamMembers for summary banners
  const allSubmitted = teamMembers.filter((m) => m.latestSubmission !== null);
  const waiting = teamMembers.filter(
    (m) => m.latestSubmission === null && !m.isOnVacation,
  );
  const needsReview = allSubmitted.filter(
    (m) => m.latestSubmission?.reviewedAt === null,
  );

  // Determine which members to show based on filter
  const visibleMembers =
    filter === "all"
      ? teamMembers
      : filter === "needs-review"
        ? needsReview
        : allSubmitted.filter((m) => m.latestSubmission?.reviewedAt !== null);

  // Group by managerId
  const groupMap = new Map<string, TeamMember[]>();
  for (const m of visibleMembers) {
    const key = m.managerId || "__none__";
    const arr = groupMap.get(key) || [];
    arr.push(m);
    groupMap.set(key, arr);
  }

  // Sort groups: "no manager" first, then alphabetical by manager name
  const groups: ManagerGroup[] = [];
  const noManagerMembers = groupMap.get("__none__");
  if (noManagerMembers && noManagerMembers.length > 0) {
    groups.push({
      managerId: null,
      managerName: "No Manager",
      members: noManagerMembers,
    });
  }
  const sortedManagerIds = [...groupMap.keys()]
    .filter((k) => k !== "__none__")
    .sort((a, b) => {
      const nameA = managerNameMap.get(a) || "";
      const nameB = managerNameMap.get(b) || "";
      return nameA.localeCompare(nameB);
    });
  for (const managerId of sortedManagerIds) {
    groups.push({
      managerId,
      managerName: managerNameMap.get(managerId) || "Unknown",
      members: groupMap.get(managerId)!,
    });
  }

  // Start with all groups collapsed — keeps focus on the summary + review CTA
  const allGroupKeys = groups.map((g) => g.managerId || "__none__");
  const collapsed = useSignal<Set<string>>(new Set(allGroupKeys));
  const showDetails = useSignal(false);

  function toggleGroup(groupKey: string) {
    const next = new Set(collapsed.value);
    if (next.has(groupKey)) {
      next.delete(groupKey);
    } else {
      next.add(groupKey);
    }
    collapsed.value = next;
  }

  // Empty states
  if (visibleMembers.length === 0 && filter !== "all") {
    return (
      <div class="flex flex-col items-center justify-center gap-3 p-8 rounded-xl border border-border bg-surface text-center">
        <div class="size-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
          <IcoCheck class="size-6 text-green-600 dark:text-green-400" />
        </div>
        {filter === "needs-review" ? (
          <>
            <h3 class="font-medium text-text">All caught up!</h3>
            <p class="text-text-muted text-sm max-w-sm">
              You've reviewed all submitted check-ins for this week.
            </p>
          </>
        ) : (
          <>
            <h3 class="font-medium text-text">No reviewed check-ins yet</h3>
            <p class="text-text-muted text-sm max-w-sm">
              Check-ins you've reviewed will appear here.
            </p>
          </>
        )}
      </div>
    );
  }

  // For group headers, compute "submitted" stats from *all* members in each group
  // (not the filter-narrowed set) so the stat is always meaningful
  const allGroupMap = new Map<string, TeamMember[]>();
  for (const m of teamMembers) {
    const key = m.managerId || "__none__";
    const arr = allGroupMap.get(key) || [];
    arr.push(m);
    allGroupMap.set(key, arr);
  }

  return (
    <>
      {/* All Complete State */}
      {filter === "all" &&
        waiting.length === 0 &&
        allSubmitted.length > 0 &&
        needsReview.length === 0 && (
          <div class="flex items-center justify-center gap-3 p-6 mb-6 rounded-xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30">
            <div class="size-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
              <IcoCheck class="size-5 text-green-600 dark:text-green-400" />
            </div>
            <span class="text-lg font-medium text-green-700 dark:text-green-400">
              All check-ins submitted and reviewed!
            </span>
          </div>
        )}

      {/* Toggle to show/hide detailed member listing */}
      <button
        type="button"
        onClick={() => {
          showDetails.value = !showDetails.value;
        }}
        class="flex items-center gap-2 text-sm text-text-muted hover:text-text transition-colors mb-4"
      >
        <IcoChevronDown
          class={`size-4 transition-transform ${!showDetails.value ? "-rotate-90" : ""}`}
        />
        <span>{showDetails.value ? "Hide" : "View"} member details</span>
      </button>

      {showDetails.value && (
        <div class="space-y-4">
          {groups.map((group) => {
            const groupKey = group.managerId || "__none__";
            const isCollapsed = collapsed.value.has(groupKey);
            // Use unfiltered group data for the header submission stat
            const allInGroup = allGroupMap.get(groupKey) || group.members;
            const groupSubmitted = allInGroup.filter(
              (m) => m.latestSubmission !== null,
            );
            const groupAway = allInGroup.filter(
              (m) => m.isOnVacation && m.latestSubmission === null,
            );
            const groupActive = allInGroup.length - groupAway.length;

            return (
              <section key={groupKey}>
                {/* Group Header */}
                <button
                  type="button"
                  onClick={() => toggleGroup(groupKey)}
                  class="w-full flex items-center gap-3 px-4 py-3 rounded-t-xl bg-surface border border-border hover:bg-surface-hover transition-colors"
                >
                  <IcoChevronDown
                    class={`size-4 text-text-muted transition-transform ${
                      isCollapsed ? "-rotate-90" : ""
                    }`}
                  />
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <span class="font-semibold text-text truncate">
                      {group.managerName}
                      {group.managerId && "'s Team"}
                    </span>
                    <span class="text-xs text-text-muted bg-surface-muted px-2 py-0.5 rounded-full">
                      {allInGroup.length}
                    </span>
                  </div>
                  <span class="text-sm text-text-muted">
                    {groupSubmitted.length}/{groupActive} submitted
                    {groupAway.length > 0 && (
                      <span class="text-sky-600 dark:text-sky-400 ml-1">
                        ({groupAway.length} away)
                      </span>
                    )}
                  </span>
                </button>

                {/* Group Members */}
                {!isCollapsed && (
                  <div class="rounded-b-xl border border-t-0 border-border bg-surface overflow-hidden divide-y divide-border border-l-3 border-l-[var(--brand-300)]">
                    {group.members.map((member) => (
                      <TeamMemberRow
                        key={member.id}
                        member={member}
                        orgId={orgId}
                        weekKey={weekKey}
                        status={
                          !member.latestSubmission
                            ? member.isOnVacation
                              ? "away"
                              : "waiting"
                            : member.latestSubmission.reviewedAt
                              ? "reviewed"
                              : "submitted"
                        }
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}

// ============================================================================
// Filter Tab
// ============================================================================

function FilterTab({
  label,
  count,
  isActive,
  href,
  showDot,
}: {
  label: string;
  count: number;
  isActive: boolean;
  href: string;
  showDot?: boolean;
}) {
  return (
    <a
      href={href}
      class={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
        isActive
          ? "bg-surface text-text shadow-sm"
          : "text-text-muted hover:text-text"
      }`}
    >
      {showDot && !isActive && (
        <span class="size-2 rounded-full bg-amber-500" aria-hidden="true" />
      )}
      {label}
      <span
        class={`text-xs ${isActive ? "text-text-muted" : "text-text-subtle"}`}
      >
        {count}
      </span>
    </a>
  );
}

// ============================================================================
// Completion Name Row (compact name for the expandable summary)
// ============================================================================

function CompletionNameRow({
  member,
  orgId,
  variant,
}: {
  member: TeamMember;
  orgId: string;
  variant: "submitted" | "waiting" | "away";
}) {
  const name = member.name || member.email;
  return (
    <a
      href={`/orgs/${orgId}/members/${member.id}`}
      class="flex items-center gap-2 py-1 px-2 -mx-2 rounded hover:bg-surface-hover transition-colors"
    >
      <div
        class={`size-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 overflow-hidden ${
          variant === "submitted"
            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            : variant === "away"
              ? "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400"
              : "bg-surface-muted text-text-muted"
        }`}
      >
        {member.profilePictureUrl ? (
          <img
            src={member.profilePictureUrl}
            alt={name}
            class="size-full object-cover"
          />
        ) : (
          name.charAt(0).toUpperCase()
        )}
      </div>
      <span
        class={`text-sm truncate ${variant === "waiting" ? "text-text-muted" : variant === "away" ? "text-sky-700 dark:text-sky-400" : "text-text"}`}
      >
        {name}
      </span>
      {variant === "away" && member.vacationEndDate && (
        <span class="text-xs text-sky-500 dark:text-sky-500 flex-shrink-0">
          until {formatVacationEndDate(member.vacationEndDate)}
        </span>
      )}
    </a>
  );
}

// ============================================================================
// Team Member Row
// ============================================================================

type MemberStatus = "waiting" | "submitted" | "reviewed" | "away";

function TeamMemberRow({
  member,
  orgId,
  weekKey,
  status,
}: {
  member: TeamMember;
  orgId: string;
  weekKey: string;
  status: MemberStatus;
}) {
  const displayName = member.name || member.email;
  const initial = displayName.charAt(0).toUpperCase();
  const hasSubmitted = status !== "waiting";
  const isReviewed = status === "reviewed";

  // Reminder state
  const sending = useSignal(false);
  const sent = useSignal(false);
  const error = useSignal<string | null>(null);

  async function handleSendReminder() {
    if (sending.value || sent.value) return;

    sending.value = true;
    error.value = null;

    try {
      await rpc.team.sendReminder({
        workspaceId: orgId,
        targetMemberId: member.id,
      });
      sent.value = true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to send";
    } finally {
      sending.value = false;
    }
  }

  // Link to the submission if exists
  const href = hasSubmitted
    ? `/orgs/${orgId}/forms/${member.latestSubmission?.formId}/submissions/${member.latestSubmission?.id}`
    : undefined;

  // Link to view this member's team (drill-down)
  const teamHref = `/orgs/${orgId}/team?week=${weekKey}&manager=${member.id}`;

  // For submitted members, make the whole card clickable via onClick
  // (using <div> instead of <a> to allow valid nested <a> links inside)
  if (hasSubmitted && href) {
    return (
      <div
        onClick={(e) => {
          // Don't navigate if user clicked on a nested link
          if ((e.target as HTMLElement).closest("a")) return;
          window.location.href = href;
        }}
        class="group flex items-center gap-4 px-5 py-4 hover:bg-surface-hover transition-colors cursor-pointer"
      >
        {/* Avatar */}
        <div
          class={`size-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ring-2 overflow-hidden ${
            isReviewed
              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 ring-green-200 dark:ring-green-800"
              : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 ring-amber-200 dark:ring-amber-800"
          }`}
        >
          {member.profilePictureUrl ? (
            <img
              src={member.profilePictureUrl}
              alt={displayName}
              class="size-full object-cover"
            />
          ) : (
            initial
          )}
        </div>

        {/* Name and Email */}
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <a
              href={`/orgs/${orgId}/members/${member.id}`}
              class="font-medium text-text truncate hover:text-[var(--brand-600)] hover:underline transition-colors"
            >
              {displayName}
            </a>
            {member.isOnVacation && (
              <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 whitespace-nowrap">
                Away
              </span>
            )}
          </div>
          <div class="flex items-center gap-2 text-sm text-text-muted truncate">
            <span class="truncate">{member.email}</span>
            {member.hasReports && (
              <>
                <span class="text-border">·</span>
                <a
                  href={teamHref}
                  class="inline-flex items-center gap-1 font-medium text-[var(--brand-600)] hover:text-[var(--brand-700)] hover:underline transition-colors whitespace-nowrap"
                >
                  <IcoUsers class="size-3.5" />
                  Team ({member.reportCount})
                </a>
              </>
            )}
          </div>
        </div>

        {/* Status */}
        <div class="flex items-center gap-3 flex-shrink-0">
          {isReviewed ? (
            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
              <IcoCheck class="size-3.5" />
              Reviewed
            </span>
          ) : (
            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
              Needs review
            </span>
          )}
          {/* Priority stats from previous period */}
          {member.priorityStats && (
            <span
              class={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                member.priorityStats.completed === member.priorityStats.total
                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                  : "bg-surface-muted text-text-muted"
              }`}
              title={`${member.priorityStats.completed} of ${member.priorityStats.total} priorities completed last week`}
            >
              <IcoCheck class="size-3" />
              {member.priorityStats.completed}/{member.priorityStats.total}
            </span>
          )}
          <span class="text-sm text-text-muted hidden sm:block">
            {formatDateShort(member.latestSubmission!.submittedAt)}
          </span>
          <IcoChevronRight class="size-5 text-text-muted group-hover:text-[var(--brand-500)] transition-colors" />
        </div>
      </div>
    );
  }

  return (
    <div class="flex items-center gap-4 px-5 py-4">
      {/* Avatar */}
      <div class="size-10 rounded-full flex items-center justify-center text-sm font-semibold bg-surface-muted text-text-muted ring-2 ring-border flex-shrink-0 overflow-hidden">
        {member.profilePictureUrl ? (
          <img
            src={member.profilePictureUrl}
            alt={displayName}
            class="size-full object-cover"
          />
        ) : (
          initial
        )}
      </div>

      {/* Name and Email */}
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <a
            href={`/orgs/${orgId}/members/${member.id}`}
            class="font-medium text-text truncate hover:text-[var(--brand-600)] hover:underline transition-colors"
          >
            {displayName}
          </a>
          {member.isOnVacation && (
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 whitespace-nowrap">
              Away
            </span>
          )}
        </div>
        <span class="text-sm text-text-muted truncate block">
          {member.email}
        </span>
      </div>

      {/* Actions */}
      <div class="flex items-center gap-3 flex-shrink-0">
        {member.hasReports && (
          <a
            href={teamHref}
            class="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--brand-600)] hover:text-[var(--brand-700)] hover:underline transition-colors"
          >
            <IcoUsers class="size-3.5" />
            Team ({member.reportCount})
          </a>
        )}
        {status === "away" ? (
          <span class="text-sm text-sky-600 dark:text-sky-400">
            {member.vacationEndDate
              ? `Back ${formatVacationEndDate(member.vacationEndDate)}`
              : "On vacation"}
          </span>
        ) : sent.value ? (
          <span class="text-sm text-green-600 dark:text-green-400 font-medium">
            Reminder sent
          </span>
        ) : error.value ? (
          <span class="text-sm text-red-600 dark:text-red-400">
            Failed to send
          </span>
        ) : (
          <button
            type="button"
            onClick={handleSendReminder}
            disabled={sending.value}
            class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-text-muted hover:text-[var(--brand-700)] hover:bg-[var(--brand-100)] transition-colors disabled:opacity-50"
          >
            <IcoMail class="size-4" />
            {sending.value ? "Sending..." : "Send reminder"}
          </button>
        )}
      </div>
    </div>
  );
}

function formatVacationEndDate(dateStr: string): string {
  const date = new Date(`${dateStr}T12:00:00`);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

// ============================================================================
// TeamMoodChart — prominent aggregate mood trend chart
// ============================================================================

function TeamMoodChart({ periods }: { periods: MoodTrendPeriod[] }) {
  // Reverse for chronological order (server returns newest-first)
  const points = [...periods].reverse();
  const latest = points[points.length - 1];

  // Chart dimensions
  const w = 600;
  const h = 200;
  const padLeft = 40;
  const padRight = 16;
  const padTop = 24;
  const padBottom = 40;
  const plotW = w - padLeft - padRight;
  const plotH = h - padTop - padBottom;

  // Fixed Y scale 1-5 for ratings
  const minY = 1;
  const maxY = 5;

  const toX = (i: number) =>
    padLeft + (i / Math.max(points.length - 1, 1)) * plotW;
  const toY = (v: number) =>
    padTop + plotH - ((v - minY) / (maxY - minY)) * plotH;

  // Build the SVG path
  const pathD = points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"} ${toX(i).toFixed(1)} ${toY(p.avgRating).toFixed(1)}`,
    )
    .join(" ");

  // Area fill under the line
  const areaD = `${pathD} L ${toX(points.length - 1).toFixed(1)} ${toY(minY).toFixed(1)} L ${toX(0).toFixed(1)} ${toY(minY).toFixed(1)} Z`;

  // Format period label for x-axis
  const formatPeriodLabel = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Y-axis ticks
  const yTicks = [1, 2, 3, 4, 5];

  // Show at most 6 x-axis labels to avoid crowding
  const maxLabels = 6;
  const labelStep = Math.max(1, Math.ceil(points.length / maxLabels));

  return (
    <div class="rounded-xl border border-border bg-surface p-5 mb-6">
      <div class="flex items-baseline justify-between mb-4">
        <div>
          <h3 class="text-base font-semibold text-text">Team Mood Trend</h3>
          <p class="text-sm text-text-muted">
            Average rating across {latest?.totalMembers ?? 0} team members
          </p>
        </div>
        {latest && (
          <div class="text-right">
            <span class="text-2xl font-bold text-[var(--brand-600)]">
              {latest.avgRating.toFixed(1)}
            </span>
            <span class="text-sm text-text-muted ml-1">/ 5</span>
            <p class="text-xs text-text-subtle">
              {latest.responseCount} of {latest.totalMembers} responded
            </p>
          </div>
        )}
      </div>

      <svg
        viewBox={`0 0 ${w} ${h}`}
        class="w-full"
        style={{ maxHeight: "200px" }}
      >
        {/* Y-axis grid lines and labels */}
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              x1={padLeft}
              y1={toY(tick)}
              x2={w - padRight}
              y2={toY(tick)}
              stroke="currentColor"
              class="text-border-muted"
              stroke-width="0.5"
              stroke-dasharray={tick === 1 || tick === 5 ? "none" : "3 3"}
            />
            <text
              x={padLeft - 8}
              y={toY(tick) + 4}
              text-anchor="end"
              class="text-text-subtle"
              font-size="11"
            >
              {tick}
            </text>
          </g>
        ))}

        {/* Area fill */}
        <path
          d={areaD}
          fill="currentColor"
          class="text-[var(--brand-100)]"
          opacity="0.5"
        />

        {/* Trend line */}
        <path
          d={pathD}
          fill="none"
          stroke="currentColor"
          class="text-[var(--brand-500)]"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />

        {/* Data dots */}
        {points.map((p, i) => {
          const isLast = i === points.length - 1;
          return (
            <circle
              key={p.periodStart}
              cx={toX(i)}
              cy={toY(p.avgRating)}
              r={isLast ? 5 : 3}
              fill={isLast ? "currentColor" : "var(--color-surface)"}
              stroke="currentColor"
              class={
                isLast ? "text-[var(--brand-600)]" : "text-[var(--brand-400)]"
              }
              stroke-width={isLast ? 2.5 : 1.5}
            />
          );
        })}

        {/* X-axis labels */}
        {points.map((p, i) => {
          if (i % labelStep !== 0) return null;
          return (
            <text
              key={p.periodStart}
              x={toX(i)}
              y={h - 8}
              text-anchor="middle"
              class="text-text-subtle"
              font-size="10"
            >
              {formatPeriodLabel(p.periodStart)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
